<script setup lang="ts">
import { ref, computed } from "vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import { useDilemmasStore } from "@/stores/modules/dilemmas/dilemmas";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { serverTimestamp } from "firebase/firestore";
import DilemmaOptionsListInput from "./DilemmaOptionsListInput.vue";
import DilemmaSettingToggle from "./DilemmaSettingToggle.vue";
import KekkleConfigBox from "@/components/ui/KekkleConfigBox.vue";
import { useI18n } from "vue-i18n";
import { setErrorToast } from "@/helpers/notifications/toast";
import type { DilemmaDocument } from "@kekkle/shared/types/documents/dilemma-document/dilemma-document";

const isOpen = defineModel<boolean>("open", { required: true });

const dilemmasStore = useDilemmasStore();
const authStore = useAuthStore();
const { t } = useI18n();

const isSubmitting = ref(false);

const form = ref({
  question: "",
  description: "",
  options: ["", ""],
  allow_revoting: false,
  allow_other: false,
});

const isAnonymous = ref(false);
const isResultsImmediate = ref(false);

const hasDuplicateOptions = computed(() => {
  const filledOptions = form.value.options
    .map((o) => o.trim().toLowerCase())
    .filter((o) => o !== "");
  return new Set(filledOptions).size !== filledOptions.length;
});

const isValid = computed(() => {
  return (
    form.value.question.trim() !== "" &&
    !form.value.options.some((o) => !o.trim()) &&
    !hasDuplicateOptions.value
  );
});

async function submitDilemma(): Promise<void> {
  if (!isValid.value || !authStore.authUuid) return;

  try {
    isSubmitting.value = true;

    const payload: Omit<DilemmaDocument, "_id"> = {
      question: form.value.question.trim(),
      options: form.value.options.map((o) => o.trim()),
      allow_other: form.value.allow_other,
      anonymity: isAnonymous.value ? "ANONYMOUS" : "NON_ANONYMOUS",
      visibility: isResultsImmediate.value ? "INSTANT" : "AFTER_ANSWERING",
      allow_revoting: isAnonymous.value ? false : form.value.allow_revoting,
      created_by: authStore.authUuid,
      created_at: serverTimestamp() as unknown as Date,
      participants: [],
      anonymous_results: {},
      answers: {},
      number_of_comments: 0,
    };

    if (form.value.description?.trim()) {
      payload.description = form.value.description.trim();
    }

    await dilemmasStore.addDilemma(payload);

    isOpen.value = false;
    // reset form
    form.value = {
      question: "",
      description: "",
      options: ["", ""],
      allow_revoting: false,
      allow_other: false,
    };
    isAnonymous.value = false;
    isResultsImmediate.value = false;
  } catch (e) {
    console.error(e);
    setErrorToast({
      message: t("pages.dilemmas.create_failed", "Failed to create dilemma"),
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="yellow"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-4">
        <h2 class="flex-1 text-2xl font-bold">
          {{ $t("pages.dilemmas.create_title", "Create Dilemma") }}
        </h2>
        <DefaultButton
          color="yellow"
          :disabled="!isValid"
          :loading="isSubmitting"
          @click="submitDilemma"
        >
          {{ $t("global.inputs.labels.save", "Save") }}
        </DefaultButton>
      </div>

      <form class="flex flex-col gap-6" @submit.prevent="submitDilemma">
        <!-- Question -->
        <TextAreaInput
          v-model="form.question"
          :label="$t('pages.dilemmas.question_label', 'What is your dilemma?')"
          :placeholder="
            $t(
              'pages.dilemmas.question_placeholder',
              'E.g., Do you prefer pizza or pancakes?',
            )
          "
          :rows="3"
        />

        <!-- Description (Optional) -->
        <TextAreaInput
          v-model="form.description"
          :label="
            $t('pages.dilemmas.description_label', 'Description (Optional)')
          "
          :placeholder="
            $t(
              'pages.dilemmas.description_placeholder',
              'Add a little more context...',
            )
          "
          :rows="2"
        />

        <!-- Options -->
        <DilemmaOptionsListInput
          v-model="form.options"
          :label="$t('pages.dilemmas.options_label', 'Options')"
        />

        <!-- Settings -->
        <KekkleConfigBox
          :title="$t('pages.dilemmas.settings_title', 'Voting Settings')"
        >
          <!-- Anonymity -->
          <DilemmaSettingToggle
            v-model="isAnonymous"
            :title="$t('pages.dilemmas.anonymous', 'Anonymous voting')"
            :hint="
              $t(
                'pages.dilemmas.anonymous_hint',
                'Nobody can see who voted for what',
              )
            "
          />

          <!-- Revoting (Only if NOT anonymous) -->
          <DilemmaSettingToggle
            v-if="!isAnonymous"
            v-model="form.allow_revoting"
            :title="$t('pages.dilemmas.allow_revoting', 'Allow changing vote')"
            :hint="
              $t(
                'pages.dilemmas.revoting_hint',
                'Users can change their minds later',
              )
            "
          />

          <!-- Allow Other -->
          <DilemmaSettingToggle
            v-model="form.allow_other"
            :title="$t('pages.dilemmas.allow_other', 'Allow custom answers')"
            :hint="
              $t(
                'pages.dilemmas.allow_other_hint',
                'Users can write their own options',
              )
            "
          />

          <!-- Visibility -->
          <DilemmaSettingToggle
            v-model="isResultsImmediate"
            :title="
              $t(
                'pages.dilemmas.results_visibility',
                'Show results immediately',
              )
            "
            :hint="
              $t(
                'pages.dilemmas.visibility_hint',
                'If disabled, users must vote first to see results',
              )
            "
          />
        </KekkleConfigBox>
      </form>
    </div>
  </KekkleBottomSheet>
</template>
